import SessionManager from '../utils/sessionManager'; // Use OLD SessionManager

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';

// Device ID helper function
function getDeviceId() {
  let deviceId = localStorage.getItem('aptirix_device_id');
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('aptirix_device_id', deviceId);
  }
  return deviceId;
}

/**
 * Secure API request function
 * Sends JWT token in Authorization header (Bearer token)
 * Also includes credentials (cookies) for backward compatibility
 */
export async function secureRequest(endpoint, options = {}) {
  const deviceId = getDeviceId();
  const currentSession = SessionManager.getCurrentSession();
  const token = currentSession?.access_token;
  
  console.log('🔐 [API] Secure API Request:', {
    endpoint,
    method: options.method || 'GET',
    deviceId,
    hasSession: !!currentSession,
    role: currentSession?.role,
    hasToken: !!token,
    tokenPreview: token ? `${token.substring(0, 20)}...` : 'NO TOKEN'
  });
  
  const headers = {
    'Content-Type': 'application/json',
    'X-Device-ID': deviceId,
    ...(options.headers || {}),
  };
  
  // CRITICAL: Add Authorization header with Bearer token
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    console.log('✅ [API] Authorization header added:', `Bearer ${token.substring(0, 30)}...`);
  } else {
    console.warn('⚠️ [API] No token available - request may fail if authentication required');
  }

  console.log('📤 [API] Request headers:', {
    'Content-Type': headers['Content-Type'],
    'X-Device-ID': headers['X-Device-ID'],
    'Authorization': headers['Authorization'] ? `Bearer ${headers['Authorization'].substring(7, 37)}...` : 'NOT SET'
  });

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include', // Also send HTTP-only cookies for backward compatibility
  });

  console.log('📥 [API] Response:', {
    endpoint,
    status: response.status,
    statusText: response.statusText,
    ok: response.ok
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    // Handle 401 Unauthorized - session expired or invalid
    if (response.status === 401) {
      console.error('❌ [API] 401 Unauthorized - Token invalid or expired');
      console.error('❌ [API] Response detail:', data?.detail);
      
      // Check if it's a device conflict
      if (data?.detail?.includes('device') || data?.detail?.includes('another')) {
        alert('⚠️ You have been logged out because you logged in from another device.');
      } else {
        alert('⚠️ Your session has expired. Please log in again.');
      }
      
      // Clear session and redirect
      SessionManager.clearAllSessions();
      
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      
      throw new Error('Session expired');
    }
    
    // Handle validation errors (422)
    let message = data?.detail || 'Something went wrong. Please try again.';
    
    if (response.status === 422 && data?.errors) {
      const errorMessages = data.errors.map(err => {
        const field = err.loc?.join('.') || 'field';
        return `${field}: ${err.msg}`;
      });
      message = errorMessages.join('; ') || message;
    } else if (response.status === 422 && typeof data?.detail === 'string') {
      message = data.detail;
    }
    
    console.error('❌ [API] Error:', {
      endpoint,
      status: response.status,
      message,
      detail: data?.detail
    });
    
    throw new Error(message);
  }

  console.log('✅ [API] Request successful');
  return data;
}

// Helper function to build query strings
function buildQueryString(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      qs.set(key, value);
    }
  });
  const query = qs.toString();
  return query ? `?${query}` : '';
}

// ============================================
// AUTH APIs
// ============================================

export function login({ email, password, role, preferredCourses }) {
  const deviceId = getDeviceId();
  return secureRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify({
      email,
      password,
      role,
      preferred_courses: preferredCourses,
      device_id: deviceId,
    }),
  });
}

export function logout() {
  return SessionManager.logout();
}

export function validateSession() {
  return SessionManager.validateSession();
}

export function checkUserExists(email) {
  return secureRequest('/auth/check-user-exists', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function signupCandidate(payload) {
  return secureRequest('/auth/signup/candidate', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function signupHr(payload) {
  return secureRequest('/auth/signup/hr', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// OTP Functions
export function sendOtp({ email }) {
  return secureRequest('/auth/send-otp', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function verifyOtp({ email, otp }) {
  return secureRequest('/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ email, otp }),
  });
}

// Password Reset
export function forgotPassword({ email, userType }) {
  return secureRequest('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({
      email,
      user_type: userType,
    }),
  });
}

export function verifyResetOTP({ email, otp, userType }) {
  return secureRequest('/auth/verify-reset-otp', {
    method: 'POST',
    body: JSON.stringify({
      email,
      otp,
      user_type: userType,
    }),
  });
}

export function resetPassword({ email, otp, newPassword, confirmPassword, userType }) {
  return secureRequest('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({
      email,
      otp,
      new_password: newPassword,
      confirm_password: confirmPassword,
      user_type: userType,
    }),
  });
}

// ============================================
// COURSE APIs
// ============================================

export function getCourses() {
  // Public endpoint - no authentication required
  // Don't use secureRequest to avoid CORS issues with credentials
  return fetch(`${API_BASE_URL}/courses`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    // Don't include credentials for public endpoints
  }).then(async (response) => {
    const data = await response.json().catch(() => ({}));
    
    if (!response.ok) {
      const message = data?.detail || 'Failed to fetch courses';
      throw new Error(message);
    }
    
    return data;
  });
}

export function enrollInCourse(courseId, paymentMode = 'Coupon', couponCode = null) {
  const session = SessionManager.getSession('candidate');
  const query = buildQueryString({ payment_mode: paymentMode });
  
  console.log('🔐 [ENROLL] Enrolling in course:', {
    courseId,
    paymentMode,
    candidateId: session?.user_id,
    hasToken: !!session?.access_token
  });
  
  return secureRequest(`/course-teaching/enroll/${courseId}${query}`, {
    method: 'POST',
    body: JSON.stringify({
      candidate_id: session?.user_id,
      payment_mode: paymentMode,
      coupon_code: couponCode
    }),
  });
}

export function checkEnrollment(courseId) {
  const session = SessionManager.getSession('candidate');
  const candidateId = session?.user_id;
  
  if (!candidateId) {
    return Promise.resolve({ is_enrolled: false });
  }
  
  // Public endpoint - no authentication required
  return fetch(`${API_BASE_URL}/course-teaching/check-enrollment/${courseId}?candidate_id=${candidateId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    // Don't include credentials for public endpoints
  }).then(async (response) => {
    const data = await response.json().catch(() => ({ is_enrolled: false }));
    
    if (!response.ok) {
      console.warn('Failed to check enrollment:', response.status);
      return { is_enrolled: false };
    }
    
    return data;
  });
}

// ============================================
// JOB APIs
// ============================================

export function getJobs(params = {}) {
  const query = buildQueryString(params);
  return secureRequest(`/jobs${query}`);
}

export function createJob(payload) {
  return secureRequest('/jobs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getJobDetails(jobId) {
  return secureRequest(`/jobs/${jobId}`);
}

export function applyToJob(jobId, payload) {
  return secureRequest(`/jobs/${jobId}/apply`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ============================================
// PROFILE APIs
// ============================================

export function getCandidateProfile(candidateId, email, manualInputParams = {}) {
  const params = new URLSearchParams();
  if (candidateId) params.set('candidate_id', candidateId);
  if (email) params.set('email', email);
  
  if (manualInputParams.manual_name) params.set('manual_name', manualInputParams.manual_name);
  if (manualInputParams.manual_email) params.set('manual_email', manualInputParams.manual_email);
  if (manualInputParams.manual_role) params.set('manual_role', manualInputParams.manual_role);
  if (manualInputParams.manual_skills) params.set('manual_skills', manualInputParams.manual_skills);
  if (manualInputParams._t) params.set('_t', manualInputParams._t);
  
  const query = params.toString();
  return secureRequest(`/candidates/profile${query ? `?${query}` : ''}`);
}

export function updateCandidateProfile(candidateId, formData) {
  const data = new FormData();
  
  if (candidateId && candidateId.trim() !== '') {
    data.append('candidate_id', candidateId);
  }
  
  data.append('full_name', formData.fullName || '');
  data.append('email', formData.email || '');
  data.append('phone', formData.phone || '');
  data.append('job_role', formData.jobRole || '');
  data.append('skills', JSON.stringify(formData.skills || []));
  
  if (formData.resume) {
    data.append('resume', formData.resume);
  }

  const deviceId = getDeviceId();

  return fetch(`${API_BASE_URL}/candidates/profile`, {
    method: 'PUT',
    headers: {
      'X-Device-ID': deviceId,
    },
    credentials: 'include',
    body: data,
  }).then(async (response) => {
    const responseData = await response.json().catch(() => ({}));
    
    if (!response.ok) {
      const message = responseData?.detail || 'Something went wrong. Please try again.';
      throw new Error(message);
    }
    return responseData;
  });
}

// ============================================
// PAYMENT APIs
// ============================================

export function createPaymentOrder(payload) {
  return secureRequest('/payments/credits/create-order', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function createCoursePaymentOrder(payload) {
  return secureRequest('/payments/courses/create-order', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function createCreditPaymentOrder(payload) {
  return secureRequest('/payments/credits/create-order', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function verifyPayment(payload) {
  return secureRequest('/payments/verify-payment', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function markPaymentFailed(payload) {
  return secureRequest('/payments/mark-payment-failed', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ============================================
// CREDIT APIs
// ============================================

export function getCreditBalance(candidateId) {
  return secureRequest(`/credits/balance?candidate_id=${candidateId}`);
}

export function claimCreditsWithPin(candidateId, pin) {
  return secureRequest(`/credits/claim-pin?candidate_id=${candidateId}`, {
    method: 'POST',
    body: JSON.stringify({ pin }),
  });
}

// ============================================
// CANDIDATE APIs (HR Dashboard)
// ============================================

export function getCandidates(filters = {}) {
  const query = buildQueryString(filters);
  return secureRequest(`/candidates/all${query}`);
}

export function decryptValue(encryptedValue) {
  // If the value is already plain (not encrypted), return it as-is
  if (!encryptedValue || typeof encryptedValue !== 'string') {
    return Promise.resolve(encryptedValue);
  }
  
  // For now, return the value as-is since backend handles encryption
  // In future, implement proper decryption if needed
  return Promise.resolve(encryptedValue);
}

export function testConnection() {
  const currentSession = SessionManager.getCurrentSession();
  const token = currentSession?.access_token;
  
  return Promise.resolve({
    success: !!token,
    encrypted_ids: false,
    message: token ? 'Connected with valid session' : 'No active session'
  });
}

// Export the secure request function as default
export default secureRequest;
